// Curated starter scenes. Drops the heavy "campus" / "mega-campus" presets
// from oq-images for now; they can be added later from saved profiles.
import type { LinkConnection, SceneObject, SceneSettings } from "./types";
import type { Profile } from "./profiles";
import type { SceneKind } from "../data/sceneKinds";
import { uid } from "./types";
import { defaultGuideSettings } from "./guides";

export const DEFAULT_SETTINGS: SceneSettings = {
  ...defaultGuideSettings(),
  groundSize: 80,
  camera: {
    targetX: 0,
    targetY: 4,
    targetZ: 0,
    yaw: Math.PI / 4,
    pitch: Math.PI / 5,
    // Tuned for the new real-world scale (turbine 80 m, warehouse 40 m).
    distance: 90,
    fov: 36,
  },
  dotGrid: true,
  dotGridSpacing: 1.4,
  dotGridOpacityLight: 0.85,
  dotGridOpacityDark: 0.35,
  // Empty string = use the theme palette default. We override the dark default
  // to white in resolveDotColor() so visitor scenes keep the white pinprick
  // floor without having to set a color per-profile.
  dotColorLight: "",
  dotColorDark: "",
  animateLinks: true,
  linkSpeed: 0.2,
  linkThickness: 0.025,
  // Empty = monochrome white (resolved at render time).
  linkColorLight: "",
  linkColorDark: "",
  linkCableOpacity: 0.55,
  linkStripOpacity: 0.6,
  linkPower: true,
  straightLinks: false,
  accentLight: "#0b72b7",
  accentDark: "#0b72b7",
  theme: "dark",
  floatEnabled: false,
  floatHeight: 0.4,
  floatShadowSize: 4.0,
  floatShadowOpacity: 0.55,
};

function place(kind: SceneKind, x: number, z: number, opts: Partial<SceneObject> = {}): SceneObject {
  return {
    id: uid(),
    kind,
    x,
    z,
    scale: 1,
    rotation: 0,
    ...opts,
  };
}

// A small starter — enough to verify scene + interactions, not a hero shot.
function starterObjects(): SceneObject[] {
  return [
    place("modular-pod",     0,  0, { accent: true }),
    place("dedicated-rack", -3,  0),
    place("dedicated-rack",  3,  0),
    place("dedicated-rack", -3,  3),
    place("dedicated-rack",  3,  3),
    place("ai-cluster",     -8,  0),
    place("ai-cluster",      8,  0),
    place("control-hub",     0, -6),
    place("inference-node",  6, -6),
    place("inference-node", -6, -6),
    place("storage-array",  10,  6),
    place("storage-array", -10,  6),
    place("wind-turbine",    0, 14),
    place("wind-turbine",   -3, 14),
    place("wind-turbine",    3, 14),
  ];
}

function starterLinks(objects: SceneObject[]): LinkConnection[] {
  // Trivial spine: link the central modular pod to neighbouring kinds.
  const center = objects.find((o) => o.kind === "modular-pod");
  if (!center) return [];
  return objects
    .filter((o) => o.id !== center.id)
    .filter((o) => ["dedicated-rack", "ai-cluster", "control-hub", "inference-node"].includes(o.kind))
    .map((o) => ({
      id: uid(),
      fromId: center.id,
      toId: o.id,
    }));
}

export function seedStarterProfile(): Profile {
  const objects = starterObjects();
  const links = starterLinks(objects);
  return {
    id: uid(),
    name: "Starter scene",
    settings: { ...DEFAULT_SETTINGS },
    objects,
    links,
    updatedAt: Date.now(),
  };
}

export function seedEmptyProfile(): Profile {
  return {
    id: uid(),
    name: "Empty",
    settings: { ...DEFAULT_SETTINGS },
    objects: [],
    links: [],
    updatedAt: Date.now(),
  };
}
